import * as React from 'react'
import Button from 'components/Button'
import Icon from 'components/Icon'
import MessageViewModel from 'messaging/components/ConversationView/MessageViewModel'
import { useState } from 'react'
import styles from './EditableMessageContent.sass'
import { Id } from 'types/definitions'
import TextAreaInput from 'components/inputs/TextArea'
import ConversationViewModel from 'messaging/components/ConversationView/ConversationViewModel'
import { observer } from 'mobx-react'

interface EditableContentProps {
  message: MessageViewModel
  isEditing: boolean
  children: React.ReactNode
  vm: ConversationViewModel
}

export default observer(function EditableContent({
  message,
  vm,
  children,
  isEditing,
}: EditableContentProps) {
  const [isHover, setIsHover] = useState(false)
  const allowEdition = vm.canEditMessage && message.isEditable
  const displayEditMessageButton = allowEdition && !isEditing && isHover

  const saveEdition = async (text: string) => {
    await vm.updateMessage(message.message.id, text)
    // since after saving the message, the hover state is still true, but we might be out of the container, we need to set it to false
    setIsHover(false)
  }

  const cancelEdition = () => {
    vm.setEditingMessageId(Id.none)
    // since after cancelling the message, the hover state is still true, but we might be out of the container, we need to set it to false
    setIsHover(false)
  }

  return (
    <div
      className={styles.editMessageContainer}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <EditMessageButton
        onClick={() => vm.setEditingMessageId(message.message.id)}
        isVisible={displayEditMessageButton}
      />
      {!isEditing ? (
        children
      ) : (
        <TextEditingContent
          text={message.message.text}
          onCancel={cancelEdition}
          onSave={saveEdition}
          loading={vm.updateMessage.pending}
        />
      )}
    </div>
  )
})

interface TextEditingContentProps {
  text: string
  onCancel: () => void
  onSave: (text: string) => void
  loading: boolean
}

function TextEditingContent({
  text,
  onCancel,
  onSave,
  loading,
}: TextEditingContentProps) {
  const [value, setValue] = useState(text)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!loading) {
        onSave(value)
      }
    }
  }

  return (
    <div className={styles.textEditContainer}>
      <TextAreaInput
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={styles.textEditInput}
        autoSize
        onKeyDown={handleKeyDown}
      />
      <div className={styles.textEditButtons}>
        <Button type="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={value.length === 0}
          loading={loading}
          onClick={() => onSave(value)}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

interface EditMessageButtonProps {
  onClick: () => void
  isVisible: boolean
}

function EditMessageButton({ onClick, isVisible }: EditMessageButtonProps) {
  return isVisible ? (
    <div className={styles.editButtonContainer}>
      <Button type="default" className={styles.editButton} onClick={onClick}>
        Edit <Icon name="chat-edit" size={30} />
      </Button>
    </div>
  ) : null
}
